import { Vec2, Vec3, Vec4 } from 'ayvri-std/lib/vector';
import { defaultValue, defined } from './helpers/index';
import { smoothstep } from './Math';
const kDebugEnabled = false; // Show element AABBs and world positions, colored according to visibility
const kHideDuration = 400; // Milliseconds over which to fade the opacity of a HUD element as it becomes (in)visible
const kTerrainOcclusionRatio = 0.1; // If this percentage of the vector between the camera and a point is inside terrain, the point is occluded
const kTerrainVisibilityLevel = 10; // The tile level to raycast against when testing visibility against terrain
const kTerrainAltitudeBias = 100; // The world position will be raised by this amount before raycasting against terrain
const scratchVec4 = new Vec4();
const scratchVec3 = new Vec3();
const scratchVec2 = new Vec2();
var OcclusionFlags;
(function (OcclusionFlags) {
    OcclusionFlags[OcclusionFlags["Hud"] = 1] = "Hud";
    OcclusionFlags[OcclusionFlags["Terrain"] = 2] = "Terrain";
})(OcclusionFlags || (OcclusionFlags = {}));
export class HudElement {
    constructor(id, name, updateFunc) {
        this.screenAABB = new Vec4(); // XY center position, ZW half-widths
        this._id = undefined; // A numeric ID which can be used to reference the corresponding HUD element in Globe
        this._updateFunc = undefined; // Called by all set*() functions to update the Globe HUD element
        this._name = undefined; // For debugging purposes it's useful to have a human readable name
        this._canvas = undefined; // An HTMLCanvasElement to draw
        this._show = true; // If show is false, this element will not be drawn
        this._scale = new Vec2(1, 1); // The element can be scaled from its original dimensions (in world or screen space)
        this._worldPos = new Vec3(1, 0, 0); // The world space position of the anchor point
        this._anchor = new Vec2(0, 1); // The anchor point in canvas space, from -1 to 1 where [0,1] is the canvas bottom center.
        this._offset = new Vec2(0, 0); // Offsets the anchor point from worldPos in screen space. Units are pixels, +Y move down.
        this._worldHeight = 0; // If set, the element is always x meters tall. Otherwise it is a constant screenspace size.
        this._opacity = 1.0; // 1 is fully opaque, 0 is fully transparent.
        this._zScale = 1.0; // 1 does not affect the screenspace z position, 0.0 sets the z position to the camera near plane
        this._visibilityTesting = false; // If true, this element will be hidden when it is occluded behind another element
        this._visibleTime = 0;
        this._lastOccluded = false;
        this._occluded = 0;
        this._id = id;
        this._updateFunc = updateFunc;
        this._name = defaultValue(name, 'Unknown');
    }
    updateVisible(occlusionFlags) {
        this._occluded = occlusionFlags;
        if (!!this._occluded !== this._lastOccluded) {
            this._visibleTime = performance.now() + kHideDuration - Math.max(0, this._visibleTime - performance.now());
        }
        const t = smoothstep(this._visibleTime - kHideDuration, this._visibleTime, performance.now());
        const opacity = this._occluded ? 1.0 - t : t;
        this._updateFunc({
            opacity: opacity * this._opacity,
            show: opacity > 0.0 && this._show,
        });
        this._lastOccluded = !!this._occluded;
    }
    setCanvas(canvas) {
        this._canvas = canvas;
        this._updateFunc({ canvas });
    }
    setWorldPos(pos) {
        this._worldPos.x = pos.x;
        this._worldPos.y = pos.y;
        this._worldPos.z = pos.z;
        this._updateFunc({ posWorld: this._worldPos });
    }
    setShow(show) {
        this._show = show;
        this._updateFunc({ show: this._show });
    }
    setScale(scale) {
        this._scale.x = scale;
        this._scale.y = scale;
        this._updateFunc({ scale: this._scale });
    }
    setScaleX(scaleX) {
        this._scale.x = scaleX;
        this._updateFunc({ scale: this._scale });
    }
    setScaleY(scaleY) {
        this._scale.y = scaleY;
        this._updateFunc({ scale: this._scale });
    }
    setAnchor(x, y) {
        this._anchor.x = x;
        this._anchor.y = y;
        if (this._canvas) {
            this._updateFunc({
                anchorOffset: {
                    x: -(x * 0.5 + 0.5),
                    y: -(y * 0.5 + 0.5),
                },
            });
        }
    }
    setOffset(x, y) {
        this._offset.x = x;
        this._offset.y = y;
        this._updateFunc({ offset: this._offset });
    }
    setWorldHeight(heightInMeters) {
        this._worldHeight = heightInMeters;
        this._updateFunc({ worldHeight: heightInMeters });
    }
    setOpacity(opacity) {
        this._opacity = opacity;
        this._updateFunc({ opacity });
    }
    setZScale(zScale) {
        this._zScale = zScale;
        this._updateFunc({ zScale });
    }
    setVisibilityTesting(testing) {
        this._visibilityTesting = testing;
    }
    get name() {
        return this._name;
    }
    get id() {
        return this._id;
    }
    get scale() {
        return this._scale;
    }
    get opacity() {
        return this._opacity;
    }
    get worldPos() {
        return this._worldPos;
    }
    get worldHeight() {
        return this._worldHeight;
    }
    get canvasWidth() {
        return this._canvas.width;
    }
    get canvasHeight() {
        return this._canvas.height;
    }
    get offset() {
        return this._offset;
    }
    get anchor() {
        return this._anchor;
    }
    get canvas() {
        return this._canvas;
    }
    get show() {
        return this._show;
    }
    get zScale() {
        return this._zScale;
    }
    get occluded() {
        return this._occluded;
    }
    get visibilityTesting() {
        return this._visibilityTesting;
    }
}
/**
 * HUD is responsible for managing 2D elements on screen. Elements may have either a world or screenspace position, and
 * they will be transformed into screenspace in HUD so that overlapping elements can be managed. All elements will
 * generate a single canvas which will be blitted onto the screen by the rendering backend.
 */
export class Hud {
    constructor() {
        this.elements = [];
        this.elementIdCount = 1;
        this.timeSliceIdx = 0;
    }
    onSceneLoad(ui, globes) {
        this.globes = globes;
        this.debugCanvas = kDebugEnabled ? ui.rootElement.appendChild(createDebugCanvas()) : undefined;
    }
    update(state, globe, camera) {
        const activeElements = this.elements.filter(e => e.show);
        const visibleElements = activeElements.filter(e => !e.visibilityTesting);
        const hideableElements = activeElements.filter(e => e.visibilityTesting);
        for (const element of activeElements) {
            // Compute approximate distance from camera (ignoring Z bias)
            element.camDist = Vec3.distanceSquared(element.worldPos, camera.pos);
            // Compute AABBs
            const rootPos = worldPosToScreenPos(camera.worldToClip, state.layout.width, state.layout.height, element.worldPos, scratchVec2);
            if (rootPos && element.show) {
                const halfWidth = element.canvas.width * element.scale.x * 0.5;
                const halfHeight = element.canvas.height * element.scale.y * 0.5;
                const centerX = rootPos.x + element.offset.x - element.anchor.x * halfWidth;
                const centerY = rootPos.y + element.offset.y - element.anchor.y * halfHeight;
                element.screenAABB.init(centerX, centerY, halfWidth, halfHeight);
            }
        }
        // Sort by camera distance (which approximates view-space depth), nearest to farthest
        hideableElements.sort((a, b) => a.camDist - b.camDist);
        // Update terrain occlusion for some number of elements
        const kTerrainTestsPerFrame = 1;
        for (let i = 0; i < kTerrainTestsPerFrame && i < hideableElements.length; i++) {
            const element = hideableElements[this.timeSliceIdx++ % hideableElements.length];
            const terrainOccluded = terrainOcclusion(globe, element.worldPos, camera.pos);
            element.updateVisible(terrainOccluded ? element.occluded | OcclusionFlags.Terrain : element.occluded & ~OcclusionFlags.Terrain);
        }
        // Determine visibility for elements that requested testing
        for (const element of hideableElements) {
            let occluded = 0;
            // Detect overlaps with other elements. Hide any conflicting elements that appear behind another element.
            occluded |= !visibleElements.every(blocker => aabbOcclusion(blocker.screenAABB, element.screenAABB) === false) ? OcclusionFlags.Hud : 0;
            occluded |= element.occluded & OcclusionFlags.Terrain;
            element.updateVisible(occluded);
            if (!element.occluded)
                visibleElements.push(element);
        }
        if (kDebugEnabled) {
            this.debugCanvas.width = this.debugCanvas.clientWidth;
            this.debugCanvas.height = this.debugCanvas.clientHeight;
            const ctx = this.debugCanvas.getContext('2d');
            for (const element of activeElements) {
                const occlusionFlags = element.occluded;
                const aabb = element.screenAABB;
                const rootPosX = aabb.x - element.offset.x + element.anchor.x * aabb.z;
                const rootPosY = aabb.y - element.offset.y + element.anchor.y * aabb.w;
                ctx.strokeStyle = occlusionFlags & OcclusionFlags.Hud ? 'red' : 'green';
                ctx.strokeRect(aabb.x - aabb.z, aabb.y - aabb.w, aabb.z * 2.0, aabb.w * 2.0);
                ctx.fillStyle = occlusionFlags & OcclusionFlags.Terrain ? 'red' : 'green';
                ctx.beginPath();
                ctx.arc(rootPosX, rootPosY, 5, 0, 2.0 * Math.PI);
                ctx.fill();
            }
        }
    }
    createElement(name) {
        const elementId = this.elementIdCount++;
        const updateFunc = options => this.globes.forEach(g => g.updateHudElement(elementId, options));
        const element = new HudElement(elementId, name, updateFunc);
        this.elements.push(element);
        this.globes.forEach(g => g.createHudElement(elementId, name));
        return element;
    }
    removeElement(element) {
        const idx = this.elements.indexOf(element);
        if (idx === -1)
            throw new Error(`HUD element does not exist: ${element}`);
        this.globes.forEach(g => g.deleteHudElement(element._id));
        this.elements.splice(idx, 1);
    }
}
function createDebugCanvas() {
    const canvas = document.createElement('canvas');
    canvas.style.position = 'absolute';
    canvas.style.width = '100vw';
    canvas.style.height = '100vh';
    canvas.style.pointerEvents = 'none';
    return canvas;
}
function worldPosToScreenPos(viewProj, screenWidth, screenHeight, worldPos, result) {
    const clipPos = viewProj.transformVec4(scratchVec4.init(worldPos.x, worldPos.y, worldPos.z, 1.0));
    Vec4.divide(clipPos, clipPos.w, clipPos);
    const zClipped = clipPos.z > 1.0 || clipPos.z < -1.0;
    if (zClipped) {
        return undefined;
    }
    else {
        result.x = (clipPos.x * 0.5 + 0.5) * screenWidth;
        result.y = (clipPos.y * -0.5 + 0.5) * screenHeight;
        return result;
    }
}
function terrainOcclusion(globe, worldPos, cameraPos) {
    // Raise the raycast target by a specified bias amount, to avoid issues with lower tile-level terrain
    const rootPos = Vec3.multiply(worldPos, 1.0 + kTerrainAltitudeBias / worldPos.length(), new Vec3());
    // Non-normalized vector from camera to root position
    const viewVec = Vec3.subtract(rootPos, cameraPos, scratchVec3);
    // Determine the rough amount of terrain that the ray passes through on the way to the root pos
    const hitPosA = globe.raycastLevel(new Vec3(), kTerrainVisibilityLevel, cameraPos, viewVec, false);
    const hitPosB = defined(hitPosA) ? globe.raycastLevel(new Vec3(), kTerrainVisibilityLevel, rootPos, viewVec.negate(), true) : undefined;
    if (defined(hitPosA) && defined(hitPosB)) {
        const undergroundLength = Vec3.distance(hitPosA, hitPosB);
        return undergroundLength > viewVec.length() * kTerrainOcclusionRatio;
    }
    else
        return false;
}
function aabbOcclusion(a, b) {
    if (Math.abs(a.x - b.x) > (a.z + b.z))
        return false;
    if (Math.abs(a.y - b.y) > (a.w + b.w))
        return false;
    // We have an overlap
    return true;
}
